import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { format, parse } from 'date-fns';
import { getColor } from '@/components/pages/AssetsStudioPage/MainContent/Alarms/utils';
// Removed unused Chakra UI import

interface ChartDatum {
  date: string;
  names: string[];
  values: number[];
}

interface StackedBarChartEchartsProps {
  type?: string;
  variant?: 'stacked' | 'regular';
  barWidth?: number;
  hideYAxis?: boolean;
  hideXAxis?: boolean;
  hideXAxisLine?: boolean;
  YAxisUnits?: string;
  gridOverwrite?: any;
  legnedOverwrite?: any;
  colorByValueRank?: boolean;
  showLegendLabels?: boolean;
  componentColorMap?: Record<string, string>;
  chartData?: ChartDatum[];
  xAxisMin?: any;
  xAxisMax?: any;
  renderTooltip?: (data: {
    chartData: ChartDatum[];
    params: any[];
    type?: string;
    colorMap: Record<string, string>;
  }) => string;
}

const defaultTooltipFormatter = (params: any[], colorMap: Record<string, string>): string => {
  return (
    params
      // .filter((p) => p.value > 0)
      .map((p) => {
        const color = colorMap[p.seriesName] || '#ccc';
        return `
        <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
          <span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:${color}"></span>
          <span>${p.seriesName}: <strong>${p.value}</strong></span>
        </div>
      `;
      })
      .join('')
  );
};

// Regular bar chart - convenience export using the same component with variant='regular'
export const BarChartEcharts = (props: Omit<StackedBarChartEchartsProps, 'variant'>) => {
  return <StackedBarChartEcharts {...props} variant="regular" />;
};

export const StackedBarChartEcharts = ({
  type = 'components',
  variant = 'stacked',
  barWidth = 10,
  hideYAxis = false,
  hideXAxis = false,
  hideXAxisLine = false,
  gridOverwrite,
  legnedOverwrite,
  colorByValueRank = false,
  chartData = [],
  showLegendLabels = true,
  componentColorMap: externalColorMap,
  YAxisUnits = '',
  renderTooltip,
  xAxisMin,
  xAxisMax,
}: StackedBarChartEchartsProps) => {
  if (!Array.isArray(chartData) || chartData.length === 0) return null;

  const dates = useMemo(() => chartData.map((el) => el.date), [chartData]);

  const componentColorMap = useMemo(() => {
    if (externalColorMap) return externalColorMap;

    const map: Record<string, string> = {};
    const seen = new Set<string>();
    let colorIndex = 0;

    chartData.forEach((entry) => {
      entry.names.forEach((name) => {
        if (!seen.has(name)) {
          map[name] = getColor(colorIndex++);
          seen.add(name);
        }
      });
    });

    return map;
  }, [externalColorMap, chartData]);

  const allComponents = useMemo(() => {
    const set = new Set<string>();
    chartData.forEach((d) => d.names.forEach((n) => set.add(n)));
    return Array.from(set);
  }, [chartData]);

  const series: any[] = [];

  if (colorByValueRank) {
    // Rank-based: one series per rank
    const maxRanks = Math.max(...chartData.map((entry) => entry.names.length));

    for (let rank = 0; rank < maxRanks; rank++) {
      const rankData = chartData.map((entry) => {
        const sorted = entry.names
          .map((name, i) => ({ name, value: entry.values[i] }))
          .sort((a, b) => b.value - a.value);
        return sorted[rank]?.value ?? 0;
      });

      series.push({
        name: `Rank ${rank + 1}`,
        type: 'bar',
        stack: variant === 'stacked' ? 'total' : undefined,
        barWidth,
        emphasis: { focus: 'series' },
        itemStyle: { color: getColor(rank) },
        data: rankData,
      });
    }
  } else {
    // Component-based: sort each date's components, but build per-component series
    const componentSeriesData: Record<string, number[]> = {};
    allComponents.forEach((component) => {
      componentSeriesData[component] = new Array(chartData.length).fill(0);
    });

    chartData.forEach((day, dayIndex) => {
      const sorted = day.names.map((name, i) => ({ name, value: day.values[i] })).sort((a, b) => b.value - a.value);

      for (const { name, value } of sorted) {
        componentSeriesData[name][dayIndex] = value;
      }
    });

    for (const component of allComponents) {
      series.push({
        name: component,
        type: 'bar',
        stack: variant === 'stacked' ? 'total' : undefined,
        barWidth,
        emphasis: { focus: 'series' },
        itemStyle: {
          color: componentColorMap[component],
        },
        data: componentSeriesData[component],
      });
    }
  }

  const option = {
    textStyle: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      fontSize: 12,
    },
    tooltip: {
      trigger: 'axis',
      confine: false,
      axisPointer: { type: 'shadow' },
      position: (
        point: number[],
        params: any[],
        dom: HTMLDivElement,
        rect: any,
        size: { contentSize: [number, number]; viewSize: [number, number] }
      ): [number, number] | 'inside' => {
        const [tooltipWidth, tooltipHeight] = size.contentSize;
        const [viewWidth, viewHeight] = size.viewSize;
        const [mouseX, mouseY] = point;

        // Position tooltip to the left if it would overflow on the right
        let x = mouseX + 10;
        let y = mouseY - tooltipHeight / 2;

        // Check right boundary
        if (x + tooltipWidth > viewWidth) {
          x = mouseX - tooltipWidth - 10;
        }

        // Check left boundary
        if (x < 0) {
          x = 10;
        }

        // Check top boundary
        if (y < 0) {
          y = 10;
        }

        // Check bottom boundary
        if (y + tooltipHeight > viewHeight) {
          y = viewHeight - tooltipHeight - 10;
        }

        return [x, y];
      },
      formatter: (params: any[]) => {
        try {
          // const nonZero = params.filter((p) => p.value > 0);
          const nonZero = params;
          if (nonZero.length === 0) return '';
          if (renderTooltip) {
            return renderTooltip({
              chartData,
              params: nonZero,
              type,
              colorMap: componentColorMap,
            });
          } else {
            return defaultTooltipFormatter(nonZero, componentColorMap);
          }
        } catch (err) {
          return '';
        }
      },
    },
    legend: {
      type: 'scroll',
      orient: 'horizontal',
      bottom: -5,
      itemGap: 0,
      pageIconSize: 0,
      pageTextStyle: { color: '#666' },
      textStyle: { fontSize: 10 },
      formatter: showLegendLabels ? undefined : () => '',
      ...legnedOverwrite,
    },
    grid: {
      top: 5,
      left: 0,
      right: 5,
      bottom: 0,
      containLabel: true,
      ...gridOverwrite,
    },
    xAxis: {
      type: 'category',
      data: dates,
      show: !hideXAxis,
      axisLabel: {
        formatter: (val: string) => {
          const date = new Date(val);
          return isNaN(date.getTime()) ? '' : format(date, 'MMM dd');
        },
        color: '#55687D',
      },
      axisTick: {
        show: !hideXAxisLine,
      },
      axisLine: {
        show: !hideXAxisLine,
      },
    },
    yAxis: {
      type: 'value',
      show: !hideYAxis,
      axisLabel: {
        formatter: (val: number) => {
          if (val > 999999) {
            return `${(val / 1_000_000).toFixed(1)}M`;
          } else if (val > 999) {
            if (val / 1000 < 10) return `${(val / 1000).toFixed(1)}K`;
            else return `${Math.round(val / 1000)}K`;
          }
          return `${val}`;
        },
        color: '#55687D',
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#F1F2F4',
          type: 'dashed',
        },
      },
    },
    series,
    dataZoom: [
      { type: 'inside', xAxisIndex: 0 },
      { type: 'select', xAxisIndex: 0 },
    ],
  };

  return (
    <ReactECharts
      key={dates.join(',')}
      option={option}
      style={{ height: '100%', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};
